import axios from 'axios';
const BASEURL = process.env.REACT_APP_BASEURL;
// Create an Axios instance with a base URL
const axiosInstance = axios.create({
  baseURL: BASEURL,
});

// Request interceptor to add accessToken to headers
axiosInstance.interceptors.request.use(
  (config) => {
    const accessToken = localStorage.getItem('accessToken');
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Flag to track refresh attempts and prevent infinite loop
let isRefreshing = false;
let refreshSubscribers = [];

// Function to notify all subscribers with the new token after a refresh
const onRrefreshed = (token) => {
  refreshSubscribers.forEach((callback) => callback(token));
  refreshSubscribers = [];
};

// Function to add requests to the queue during token refresh
const addRefreshSubscriber = (callback) => {
  refreshSubscribers.push(callback);
};

// Response interceptor to handle 401 errors and refresh token
axiosInstance.interceptors.response.use(
  (response) => response, // If response is successful, simply return it
  async (error) => {
    const originalRequest = error.config;

    // Check if error status is 401 and the request hasn't been retried
    if (error.response && error.response.status === 401 && !originalRequest._retry) {
      if (isRefreshing) {
        // If a refresh is already in progress, queue the request
        return new Promise((resolve) => {
          addRefreshSubscriber((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            resolve(axiosInstance(originalRequest));
          });
        });
      }

      // Mark the original request as retried and start token refresh process
      originalRequest._retry = true;
      isRefreshing = true;

      try {
        // Make a request to the refresh endpoint with credentials
        const { data } = await axiosInstance.post('/auth/refresh', {
            refreshToken: localStorage.getItem('refreshToken'),
        });

        // Save the new accessToken in localStorage
        const newAccessToken = data.accessToken;
        const newRefreshToken = data.refreshToken;
        localStorage.setItem('accessToken', newAccessToken);
        localStorage.setItem('refreshToken', newRefreshToken);

        // Update Authorization header and notify all queued requests
        onRrefreshed(newAccessToken);
        isRefreshing = false;

        // Retry the original request with new accessToken
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return axiosInstance(originalRequest);
      } catch (refreshError) {
        // Log the refresh error
        ////console.error('Refresh token failed:', refreshError);
        isRefreshing = false;
        localStorage.removeItem('accessToken');
        localStorage.removeItem('refreshToken'); // Clear refresh token as well   
        return Promise.reject(refreshError);
      }
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
